// Difficulty presets. Easy is the tuned baseline; harder levels make monsters tougher and
// upgrades pricier while paying less gold. Each difficulty has its own save slot.
export type DifficultyId = 'easy' | 'medium' | 'tough' | 'hard';

export interface DifficultyDef {
  id: DifficultyId;
  name: string;
  blurb: string;
  /** Flat monster/boss HP multiplier. */
  hpMult: number;
  /** Extra HP growth per stage, compounding: HP x hpGrowth^(stage - 1). Makes deep stages harder too. */
  hpGrowth: number;
  /** Gold from kills (and offline) multiplier. */
  goldMult: number;
  /** Price multiplier for heroes, Sword Master and skill levels. */
  costMult: number;
}

export const DIFFICULTIES: readonly DifficultyDef[] = [
  { id: 'easy',   name: 'Easy',   blurb: 'The original balance.',
    hpMult: 1,   hpGrowth: 1,     goldMult: 1,    costMult: 1 },
  // Rough "kill time vs Easy" = hpMult x sqrt(costMult / goldMult): Medium ~1.9x, Tough ~2.6x, Hard ~3.5x early on,
  // widening at deeper stages through hpGrowth.
  { id: 'medium', name: 'Medium', blurb: 'Monsters have 1.6x HP (rising deeper in), 10% less coins, upgrades cost 1.25x.',
    hpMult: 1.6, hpGrowth: 1.0015, goldMult: 0.9,  costMult: 1.25 },
  { id: 'tough',  name: 'Tough',  blurb: 'Between Medium and Hard: 2x HP (rising deeper in), 15% less coins, upgrades cost 1.4x.',
    hpMult: 2,   hpGrowth: 1.00175, goldMult: 0.85, costMult: 1.4 },
  { id: 'hard',   name: 'Hard',   blurb: 'Monsters have 2.5x HP (rising faster deeper in), 20% less coins, upgrades cost 1.6x.',
    hpMult: 2.5, hpGrowth: 1.002, goldMult: 0.8,  costMult: 1.6 },
];

/** The difficulty the shipped game runs on (chosen after playtesting Easy/Medium/Tough/Hard). */
export const GAME_DIFFICULTY: DifficultyId = 'tough';

export const DIFFICULTY_BY_ID: ReadonlyMap<DifficultyId, DifficultyDef> = new Map(DIFFICULTIES.map((d) => [d.id, d]));

export function difficultyOf(id: unknown): DifficultyDef {
  return DIFFICULTY_BY_ID.get(id as DifficultyId) ?? DIFFICULTIES[0]!;
}
